import { getLoggerInstance } from '@idmc-ms-fabric/common';
import { ITask } from '@infa-service/mdp-orchestration-api';
import { isNull, isUndefined, memoize } from 'lodash-es';
import React from 'react';
import { v5 } from 'uuid';

import { StepStatus } from '../component/core/step-progress/StepProgress';
import { ITaskDetails } from '../routes/idqa-details/explorer/explorer-utils';

interface IDecimalFormatOptions {
	minimumFractionDigits?: number;
	maximumFractionDigits?: number;
}

export const decimalFormat = (() => {
	const key = (minimumFractionDigits: number, maximumFractionDigits: number) => `${minimumFractionDigits}_${maximumFractionDigits}`;

	const formatters: { [x: string]: Intl.NumberFormat } = {};
	const getFormatter = (minimumFractionDigits: number, maximumFractionDigits: number) => {
		const s = key(minimumFractionDigits, maximumFractionDigits);
		if (!formatters[s]) {
			formatters[s] = new Intl.NumberFormat(window.navigator.language || 'en', {
				minimumFractionDigits,
				maximumFractionDigits,
			});
		}
		return formatters[s];
	};

	return (n: string | number, { minimumFractionDigits = 0, maximumFractionDigits = 2 }: IDecimalFormatOptions = {}) => {
		const k = +n;
		if (Number.isNaN(k)) {
			return '';
		}
		return getFormatter(minimumFractionDigits, maximumFractionDigits).format(k);
	};
})();

export const percentageFormat = (n: number | string, options?: IDecimalFormatOptions) => {
	if (isNull(n) || isUndefined(n)) {
		return '-';
	}
	const s = decimalFormat(n, options);
	return s === '' ? '' : `${s}%`;
};

export const convertToString = (value: string | null | undefined | number): string => {
	if (isNull(value) || isUndefined(value)) {
		return '-';
	}
	if (typeof value === 'string') {
		return value.length == 0 ? '-' : value;
	}
	return value.toString();
};
export const formatLargeNumber = (num: number, options?: { language?: string; maximumFractionDigits?: number }) => {
	const { language, maximumFractionDigits = 1 } = options || {};
	const lang = language || window.navigator.language || 'en';
	return new Intl.NumberFormat(lang, {
		notation: 'compact',
		maximumFractionDigits,
	}).format(num);
};

export const useLoadingViaCount = () => {
	const [count, setCount] = React.useState(0);
	const showLoading = React.useCallback(() => setCount((current) => current + 1), []);
	const hideLoading = React.useCallback(() => setCount((current) => current - 1), []);

	return { isLoading: count !== 0, showLoading, hideLoading };
};

export const noop = () => {};

export const isEmpty = (value: string) => value === undefined || value === null || value === '';

const informatica_namespace_uuid = '2bfab5b6-63ff-4e5a-9ac7-acf63e113bb5';
export const getInfaHash = memoize((id: string) => v5(id, informatica_namespace_uuid));

export const getHash = memoize((id: string, namespace: string) => v5(id, namespace));

export const generateArtifactName = (srcArtifactId: string, workspaceId: string) => `data-quality-${getHash(srcArtifactId, getInfaHash(workspaceId))}`;

export type AsyncPollingConfig<PayloadType> = { shopOnVisibilityChange?: boolean; interval: number; onChange: (payload: PayloadType) => void };
export abstract class AsyncPolling<PayloadType> {
	private timer?: ReturnType<typeof setInterval>;
	private paused = false;
	private isPreviousCallActive = false;
	constructor(private config: AsyncPollingConfig<PayloadType>) {}
	async start(): Promise<void> {
		document.addEventListener('visibilitychange', this.onVisibilityChange);
		this.timer = setInterval(async () => {
			if ((this.paused && this.config.shopOnVisibilityChange === true) || this.isPreviousCallActive) {
				return;
			}
			this.isPreviousCallActive = true;
			const data = await this.poll().finally(() => (this.isPreviousCallActive = false));
			this.config.onChange(data);
		}, this.config.interval);
	}
	stop(): void {
		document.removeEventListener('visibilitychange', this.onVisibilityChange);
		if (this.timer !== undefined) {
			clearInterval(this.timer);
		}
	}
	private onVisibilityChange(): void {
		this.paused = document.visibilityState === 'hidden';
	}
	abstract poll(): Promise<PayloadType>;
}

export class FetchError extends Error {
	constructor(private resp: Awaited<ReturnType<typeof fetch>>) {
		super('Fetch call failed.');
	}
	getResponse() {
		return this.resp;
	}
	async getResponseDetails<T>(): Promise<T> {
		return this.resp.json() as T;
	}
}

export type BEWLErrorResponse = {
	errorCode: string;
	debugMessage: string;
};

export const fetchWrapper = {
	async get<T>(url: string, headers?: HeadersInit) {
		const resp = await fetch(url, {
			headers: {
				...headers,
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
		});
		if (resp.ok) {
			return resp.json() as T;
		} else {
			const logger = getLoggerInstance({});
			const fetchError = new FetchError(resp);
			logger.error('Fetch Failed', { method: 'GET', url });
			throw fetchError;
		}
	},
	async post<Req, Resp>(url: string, body: Req, headers?: HeadersInit) {
		const resp = await fetch(url, {
			method: 'POST',
			body: JSON.stringify(body),
			headers: {
				...headers,
				'Content-Type': 'application/json',
				'Accept': 'application/json',
			},
		});
		if (resp.ok) {
			return resp.json() as Resp;
		} else {
			const logger = getLoggerInstance({});
			const fetchError = new FetchError(resp);
			logger.error('Fetch Failed', { method: 'POST', url });
			throw fetchError;
		}
	},
};

export const devMode = import.meta.env.MODE === 'development';

export const delay = (milliSeconds: number) =>
	new Promise((r: (value: boolean) => void) => {
		setTimeout(() => r(true), milliSeconds);
	});

const sortTasks = (a: ITaskDetails, b: ITaskDetails): number => {
	if ((a.taskOrder || 0) !== (b.taskOrder || 0)) {
		return (a.taskOrder || 0) - (b.taskOrder || 0);
	}
	if (a.message && b.message && (a?.message || 0) !== (b?.message || 0)) {
		return a.message.localeCompare(b.message);
	}

	if ((a?.startTime || 0) !== (b?.startTime || 0)) {
		return new Date(a?.startTime || 0).getTime() - new Date(b?.startTime || 0).getTime();
	}
	return 1;
};
export const createHierarchicalTree = (tasks: ITask[]) => {
	const tasksList: ITaskDetails[] = [...tasks];
	const map: Record<any, any> = {};
	let node;
	const roots = [];
	for (let i = 0; i < tasksList?.length; i++) {
		map[tasksList[i].id as string] = i;
		tasksList[i].childrens = [];
	}
	for (let i = 0; i < tasksList.length; i++) {
		node = tasksList[i];
		if (node.parentTaskId) {
			tasksList[map[node?.parentTaskId]]?.childrens?.push({
				id: node.id as string,
				message: node.name as string,
				state: node.status as unknown as StepStatus,
				startTime: node.startTime,
			});
			if (tasksList[map[node?.parentTaskId]]?.childrens?.length) {
				tasksList[map[node?.parentTaskId]]?.childrens?.sort(sortTasks);
			}
		} else {
			roots.push(node);
			roots?.sort(sortTasks);
		}
	}
	return roots;
};
